import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Send, ArrowLeft, MessageCircle, MapPin } from "lucide-react";
import { api, formatApiError, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, Loading } from "@/components/EmptyState";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";

export default function Chat() {
  const { jobId } = useParams();
  if (jobId) return <Conversation jobId={jobId} />;
  return <ChatList />;
}

function ChatList() {
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/chats").then(({ data }) => setChats(data)).finally(() => setLoading(false)); }, []);
  if (loading) return <Loading />;
  return (
    <div data-testid="chat-list">
      <h1 className="text-2xl sm:text-3xl font-extrabold mb-5">Pesan</h1>
      {chats.length === 0 ? (
        <EmptyState icon={MessageCircle} title="Belum ada percakapan" description="Chat tersedia setelah pelamar diterima." />
      ) : (
        <div className="space-y-3">
          {chats.map((c) => (
            <div key={c.job.id} data-testid={`chat-item-${c.job.id}`} onClick={() => navigate(`/pesan/${c.job.id}`)}
              className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between hover:border-slate-300 cursor-pointer transition-colors">
              <div className="min-w-0">
                <p className="font-semibold text-slate-900 truncate">{c.job.title}</p>
                <p className="text-sm text-slate-500 truncate">{c.last_message ? c.last_message.message : "Belum ada pesan"}</p>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <StatusBadge status={c.job.status} />
                {c.unread > 0 && <span className="bg-rose-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">{c.unread}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Conversation({ jobId }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const endRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimerRef = useRef(null);
  const lastTypingRef = useRef(0);
  const [typingName, setTypingName] = useState(null);
  const [onlineIds, setOnlineIds] = useState([]);
  const [participants, setParticipants] = useState([]);

  const appendMsg = useCallback((m) => {
    setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
  }, []);

  useEffect(() => {
    let poll;
    (async () => {
      try {
        const { data } = await api.get(`/jobs/${jobId}`);
        setJob(data);
        const msgs = await api.get(`/jobs/${jobId}/messages`);
        setMessages(msgs.data);
      } catch (e) { setError(formatApiError(e)); }
      finally { setLoading(false); }
    })();

    const token = localStorage.getItem("andil_token");
    const wsBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
    let ws;
    try {
      ws = new WebSocket(`${wsBase}/api/ws/jobs/${jobId}?token=${token}`);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try {
          const d = JSON.parse(ev.data);
          if (d.type === "message") {
            appendMsg(d);
            if (d.sender_id !== user.id) setTypingName(null);
          } else if (d.type === "typing" && d.sender_id !== user.id) {
            setTypingName(d.name || "Pengguna");
            clearTimeout(typingTimerRef.current);
            typingTimerRef.current = setTimeout(() => setTypingName(null), 2500);
          } else if (d.type === "presence") {
            setOnlineIds(d.online || []);
            if (d.participants) setParticipants(d.participants);
          }
        } catch {}
      };
    } catch {}

    // Fallback polling only when the socket is not connected
    poll = setInterval(async () => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;
      try { const { data } = await api.get(`/jobs/${jobId}/messages`); setMessages(data); } catch {}
    }, 6000);

    return () => { clearInterval(poll); clearTimeout(typingTimerRef.current); try { ws && ws.close(); } catch {} };
  }, [jobId, appendMsg, user.id]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, typingName]);

  const onType = (e) => {
    setText(e.target.value);
    const ws = wsRef.current;
    const now = Date.now();
    if (ws && ws.readyState === WebSocket.OPEN && now - lastTypingRef.current > 1500) {
      lastTypingRef.current = now;
      ws.send(JSON.stringify({ type: "typing" }));
    }
  };

  const send = async (e) => {
    e.preventDefault();
    const msg = text.trim();
    if (!msg) return;
    setText("");
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ message: msg }));
    } else {
      try { const { data } = await api.post(`/jobs/${jobId}/messages`, { message: msg }); appendMsg(data); }
      catch (err) { toast.error(formatApiError(err)); setText(msg); }
    }
  };

  if (loading) return <Loading />;
  if (error) return <EmptyState icon={MessageCircle} title="Tidak dapat mengakses chat" description={error} actionLabel="Kembali" onAction={() => navigate("/pesan")} />;

  return (
    <div className="max-w-2xl mx-auto flex flex-col" style={{ height: "calc(100vh - 10rem)" }} data-testid="conversation">
      <div className="flex items-center gap-3 pb-3 border-b border-slate-200">
        <button onClick={() => navigate("/pesan")} className="text-slate-500"><ArrowLeft size={20} /></button>
        <div className="min-w-0 flex-1 cursor-pointer" onClick={() => navigate(`/pekerjaan/${jobId}`)}>
          <p className="font-bold text-slate-900 truncate">{job?.title}</p>
          <p className="text-xs flex items-center gap-1.5">
            {(() => {
              const others = participants.filter((p) => p.id !== user.id);
              const anyOnline = others.some((p) => onlineIds.includes(p.id));
              if (anyOnline) return (<><span className="w-2 h-2 rounded-full bg-emerald-500" data-testid="online-dot" /><span className="text-emerald-600 font-medium">Online</span></>);
              const lastSeens = others.map((p) => p.last_seen).filter(Boolean).sort();
              const last = lastSeens[lastSeens.length - 1];
              return (<><span className="w-2 h-2 rounded-full bg-slate-300" data-testid="online-dot" /><span className="text-slate-400">{last ? `aktif ${timeAgo(last)}` : "Offline"}</span></>);
            })()}
            <span className="text-slate-300">·</span>
            <span className="text-slate-500 flex items-center gap-1"><MapPin size={11} /> {job?.city}</span>
          </p>
        </div>
        {job && <StatusBadge status={job.status} />}
      </div>

      <div className="flex-1 overflow-y-auto py-4 space-y-3 no-scrollbar">
        {messages.length === 0 && <p className="text-center text-slate-400 text-sm py-8">Belum ada pesan. Mulai percakapan!</p>}
        {messages.map((m) => {
          const mine = m.sender_id === user.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${mine ? "bg-blue-600 text-white rounded-br-md" : "bg-white border border-slate-200 text-slate-800 rounded-bl-md"}`}>
                <p className="text-sm whitespace-pre-wrap break-words">{m.message}</p>
                <p className={`text-[10px] mt-1 ${mine ? "text-blue-200" : "text-slate-400"}`}>{timeAgo(m.created_at)}</p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {typingName && (
        <div className="flex items-center gap-1.5 px-1 pb-1" data-testid="typing-indicator">
          <span className="flex gap-1">
            <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
            <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
            <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
          </span>
          <span className="text-xs text-slate-400 italic">{typingName} sedang mengetik…</span>
        </div>
      )}

      <form onSubmit={send} className="flex gap-2 pt-3 border-t border-slate-200">
        <Input data-testid="chat-input" value={text} onChange={onType} placeholder="Tulis pesan..." className="h-11" />
        <Button data-testid="chat-send" type="submit" className="bg-blue-600 hover:bg-blue-700 rounded-lg h-11 px-4"><Send size={18} /></Button>
      </form>
    </div>
  );
}
